import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import { useClient } from '../../context/ClientContext';
import { invoiceService } from '../../services/invoiceService';

const {
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiFileText,
  FiLoader,
  FiPlus,
  FiSave,
  FiSend,
  FiTrash2,
  FiUser,
  FiX,
} = FiIcons;

/*
 * ---------------------------------------------------------
 * HELPERS
 * ---------------------------------------------------------
 */

const createItemId = () => {
  if (
    typeof crypto !== 'undefined' &&
    crypto.randomUUID
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
};

const createEmptyItem = () => ({
  id: createItemId(),
  description: '',
  quantity: 1,
  unitAmount: '',
});

const getToday = () => {
  return new Date().toISOString().slice(0, 10);
};

const addDays = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00`);

  date.setDate(date.getDate() + days);

  return date.toISOString().slice(0, 10);
};

const calculateDaysBetween = (
  startDate,
  endDate
) => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = new Date(
    `${startDate}T00:00:00`
  );

  const end = new Date(
    `${endDate}T00:00:00`
  );

  const difference =
    end.getTime() - start.getTime();

  return Math.max(
    0,
    Math.round(
      difference / 86400000
    )
  );
};

const formatMoney = (
  value,
  currency = 'USD'
) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency:
          String(
            currency || 'USD'
          ).toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(number);
  } catch {
    return `${currency} ${number.toFixed(2)}`;
  }
};

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';

const labelClass =
  'mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

/*
 * ---------------------------------------------------------
 * CREATE INVOICE PAGE
 * ---------------------------------------------------------
 */

export default function CreateInvoice() {
  const navigate = useNavigate();

  const { activeClient } = useClient();

  /*
   * Invoice/customer bookkeeping data belongs to
   * the selected client organization.
   *
   * Stripe Connect ownership is resolved securely
   * by the backend.
   */
  const organizationId =
    activeClient?.id;

  const defaultCurrency =
    String(
      activeClient?.currency ||
        activeClient?.billing_currency ||
        activeClient?.billingCurrency ||
        'USD'
    ).toUpperCase();

  /*
   * -------------------------------------------------------
   * LOADING STATE
   * -------------------------------------------------------
   */

  const [
    pageLoading,
    setPageLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    savingMode,
    setSavingMode
  ] = useState(null);

  const [
    error,
    setError
  ] = useState('');

  /*
   * -------------------------------------------------------
   * CUSTOMER STATE
   * -------------------------------------------------------
   */

  const [
    customers,
    setCustomers
  ] = useState([]);

  const [
    customerMode,
    setCustomerMode
  ] = useState('existing');

  const [
    customerId,
    setCustomerId
  ] = useState('');

  const [
    newCustomer,
    setNewCustomer
  ] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
  });

  /*
   * -------------------------------------------------------
   * INVOICE STATE
   * -------------------------------------------------------
   */

  const [
    invoiceNumber,
    setInvoiceNumber
  ] = useState('');

  const [
    issueDate,
    setIssueDate
  ] = useState(getToday());

  const [
    dueDate,
    setDueDate
  ] = useState(
    addDays(getToday(), 30)
  );

  const [
    currency,
    setCurrency
  ] = useState(
    defaultCurrency
  );

  const [
    items,
    setItems
  ] = useState([
    createEmptyItem(),
  ]);

  const [
    taxAmount,
    setTaxAmount
  ] = useState('');

  const [
    discountAmount,
    setDiscountAmount
  ] = useState('');

  const [
    memo,
    setMemo
  ] = useState('');

  const [
    footer,
    setFooter
  ] = useState(
    'Thank you for your business.'
  );

  /*
   * -------------------------------------------------------
   * LOAD PAGE
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!organizationId) {
      setPageLoading(false);
      return;
    }

    let cancelled = false;

    const loadPage = async () => {
      try {
        setPageLoading(true);
        setError('');

        const [
          customerData,
          nextInvoiceNumber,
        ] = await Promise.all([
          invoiceService.getCustomers(
            organizationId
          ),

          invoiceService.getNextInvoiceNumber(
            organizationId
          ),
        ]);

        if (cancelled) {
          return;
        }

        setCustomers(
          customerData || []
        );

        setInvoiceNumber(
          nextInvoiceNumber || ''
        );

        if (
          !customerData ||
          customerData.length === 0
        ) {
          setCustomerMode('new');
        }
      } catch (loadError) {
        console.error(
          '[CreateInvoice] Load error:',
          loadError
        );

        if (!cancelled) {
          setError(
            loadError?.message ||
              'Unable to prepare the invoice.'
          );
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  /*
   * -------------------------------------------------------
   * DEFAULT CURRENCY
   * -------------------------------------------------------
   */

  useEffect(() => {
    setCurrency(defaultCurrency);
  }, [defaultCurrency]);

  /*
   * -------------------------------------------------------
   * SELECTED CUSTOMER
   * -------------------------------------------------------
   */

  const selectedCustomer =
    useMemo(() => {
      return customers.find(
        (customer) =>
          customer.id === customerId
      );
    }, [
      customers,
      customerId,
    ]);

  useEffect(() => {
    if (
      selectedCustomer?.currency
    ) {
      setCurrency(
        String(
          selectedCustomer.currency
        ).toUpperCase()
      );
    }
  }, [selectedCustomer]);

  /*
   * -------------------------------------------------------
   * TOTALS
   * -------------------------------------------------------
   */

  const totals =
    useMemo(() => {
      const subtotal =
        items.reduce(
          (sum, item) => {
            const quantity =
              Number(
                item.quantity || 0
              );

            const rate =
              Number(
                item.unitAmount || 0
              );

            if (
              !Number.isFinite(
                quantity
              ) ||
              !Number.isFinite(rate)
            ) {
              return sum;
            }

            return (
              sum +
              quantity * rate
            );
          },
          0
        );

      const tax =
        Number(
          taxAmount || 0
        );

      const discount =
        Number(
          discountAmount || 0
        );

      const safeTax =
        Number.isFinite(tax)
          ? tax
          : 0;

      const safeDiscount =
        Number.isFinite(
          discount
        )
          ? discount
          : 0;

      const total =
        Math.max(
          0,
          subtotal +
            safeTax -
            safeDiscount
        );

      return {
        subtotal,
        tax: safeTax,
        discount:
          safeDiscount,
        total,
      };
    }, [
      items,
      taxAmount,
      discountAmount,
    ]);

  /*
   * -------------------------------------------------------
   * ITEM ACTIONS
   * -------------------------------------------------------
   */

  const handleAddItem = () => {
    setItems((current) => [
      ...current,
      createEmptyItem(),
    ]);
  };

  const handleUpdateItem = (
    itemId,
    field,
    value
  ) => {
    setItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const handleRemoveItem = (
    itemId
  ) => {
    setItems((current) => {
      if (current.length <= 1) {
        return current;
      }

      return current.filter(
        (item) =>
          item.id !== itemId
      );
    });
  };

  /*
   * -------------------------------------------------------
   * NEW CUSTOMER
   * -------------------------------------------------------
   */

  const updateNewCustomer = (
    field,
    value
  ) => {
    setNewCustomer(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  /*
   * -------------------------------------------------------
   * VALIDATION
   * -------------------------------------------------------
   */

  const validate = (
    shouldSend = false
  ) => {
    if (!organizationId) {
      throw new Error(
        'No active client organization is selected.'
      );
    }

    if (!invoiceNumber.trim()) {
      throw new Error(
        'Invoice number is required.'
      );
    }

    if (
      customerMode ===
        'existing' &&
      !customerId
    ) {
      throw new Error(
        'Please select a customer.'
      );
    }

    if (
      customerMode === 'new'
    ) {
      if (
        !newCustomer.name.trim()
      ) {
        throw new Error(
          'Customer name is required.'
        );
      }

      if (
        !newCustomer.email.trim()
      ) {
        throw new Error(
          'Customer email is required.'
        );
      }
    }

    /*
     * Save & Send requires an email for an
     * existing customer too.
     */
    if (
      shouldSend &&
      customerMode ===
        'existing' &&
      !selectedCustomer?.email?.trim()
    ) {
      throw new Error(
        'The selected customer does not have an email address.'
      );
    }

    if (!issueDate) {
      throw new Error(
        'Issue date is required.'
      );
    }

    if (!dueDate) {
      throw new Error(
        'Due date is required.'
      );
    }

    if (
      dueDate < issueDate
    ) {
      throw new Error(
        'Due date cannot be before the issue date.'
      );
    }

    if (
      !Array.isArray(items) ||
      items.length === 0
    ) {
      throw new Error(
        'Add at least one line item.'
      );
    }

    for (
      let index = 0;
      index < items.length;
      index += 1
    ) {
      const item =
        items[index];

      if (
        !item.description.trim()
      ) {
        throw new Error(
          `Description is required for line item ${
            index + 1
          }.`
        );
      }

      const quantity =
        Number(
          item.quantity
        );

      if (
        !Number.isFinite(
          quantity
        ) ||
        quantity <= 0
      ) {
        throw new Error(
          `Quantity must be greater than zero for line item ${
            index + 1
          }.`
        );
      }

      const rate =
        Number(
          item.unitAmount
        );

      if (
        !Number.isFinite(rate) ||
        rate < 0
      ) {
        throw new Error(
          `Enter a valid rate for line item ${
            index + 1
          }.`
        );
      }
    }

    if (
      totals.subtotal <= 0
    ) {
      throw new Error(
        'Invoice subtotal must be greater than zero.'
      );
    }

    if (
      totals.total <= 0
    ) {
      throw new Error(
        'Invoice total must be greater than zero.'
      );
    }
  };

  /*
   * -------------------------------------------------------
   * CREATE LOCAL DRAFT
   *
   * Used by both Save Draft and Save & Send.
   * -------------------------------------------------------
   */

  const createInvoiceDraft =
    async (shouldSend = false) => {
      validate(shouldSend);

      let finalCustomerId =
        customerId;

      /*
       * Create the customer first when using
       * the New Customer form.
       */
      if (
        customerMode === 'new'
      ) {
        const createdCustomer =
          await invoiceService.createCustomer(
            organizationId,
            {
              name:
                newCustomer.name.trim(),

              email:
                newCustomer.email.trim(),

              phone:
                newCustomer.phone.trim(),

              companyName:
                newCustomer.companyName.trim(),

              addressLine1:
                newCustomer.addressLine1.trim(),

              addressLine2:
                newCustomer.addressLine2.trim(),

              city:
                newCustomer.city.trim(),

              state:
                newCustomer.state.trim(),

              postalCode:
                newCustomer.postalCode.trim(),

              country:
                newCustomer.country
                  .trim()
                  .toUpperCase(),

              currency,
            }
          );

        finalCustomerId =
          createdCustomer.id;
      }

      const daysUntilDue =
        calculateDaysBetween(
          issueDate,
          dueDate
        );

      return invoiceService.createInvoice(
        organizationId,
        {
          customerId:
            finalCustomerId,

          invoiceNumber:
            invoiceNumber.trim(),

          issueDate,

          dueDate,

          currency,

          collectionMethod:
            'send_invoice',

          daysUntilDue,

          taxAmount:
            totals.tax,

          discountAmount:
            totals.discount,

          memo,

          footer,
        },

        items.map(
          (item) => ({
            description:
              item.description.trim(),

            quantity:
              Number(
                item.quantity
              ),

            unitAmount:
              Number(
                item.unitAmount
              ),
          })
        )
      );
    };

  /*
   * -------------------------------------------------------
   * SAVE DRAFT
   * -------------------------------------------------------
   */

  const handleSaveDraft =
    async () => {
      if (saving) {
        return;
      }

      try {
        setSaving(true);
        setSavingMode('draft');
        setError('');

        const invoice =
          await createInvoiceDraft(
            false
          );

        console.log(
          '[CreateInvoice] Draft created:',
          invoice
        );

        navigate(
          '/sales/invoices',
          {
            replace: true,
          }
        );
      } catch (saveError) {
        console.error(
          '[CreateInvoice] Save error:',
          saveError
        );

        setError(
          saveError?.message ||
            'Unable to save the invoice.'
        );

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } finally {
        setSaving(false);
        setSavingMode(null);
      }
    };

  /*
   * -------------------------------------------------------
   * SAVE & SEND
   *
   * 1. Create the normal ProfitYO draft.
   * 2. Send that exact invoice through the existing
   *    stripe-send-invoice Edge Function.
   * 3. Stripe finalizes/hosts the invoice.
   * 4. SendGrid sends the customer email.
   * -------------------------------------------------------
   */

  const handleSaveAndSend =
    async () => {
      if (saving) {
        return;
      }

      let createdInvoice =
        null;

      try {
        setSaving(true);
        setSavingMode('send');
        setError('');

        /*
         * First create the exact same local draft
         * used by Save Draft.
         */
        createdInvoice =
          await createInvoiceDraft(
            true
          );

        if (
          !createdInvoice?.id
        ) {
          throw new Error(
            'The invoice was created without a valid invoice ID.'
          );
        }

        console.log(
          '[CreateInvoice] Draft created before sending:',
          createdInvoice
        );

        /*
         * Now send the already-created invoice.
         *
         * The backend resolves the managing accounting
         * organization and Stripe Connect account.
         */
        const sentInvoice =
          await invoiceService.sendInvoice(
            organizationId,
            createdInvoice.id
          );

        console.log(
          '[CreateInvoice] Invoice sent:',
          sentInvoice
        );

        navigate(
          '/sales/invoices',
          {
            replace: true,
          }
        );
      } catch (sendError) {
        console.error(
          '[CreateInvoice] Save & Send error:',
          sendError
        );

        const message =
          sendError?.message ||
          'Unable to save and send the invoice.';

        /*
         * If the invoice was already created locally,
         * do NOT leave the user on this form.
         *
         * Retrying from this page could create another
         * customer/invoice. Return to Invoices where the
         * existing record can be inspected safely.
         */
        if (
          createdInvoice?.id
        ) {
          window.alert(
            `${message}\n\nThe invoice was saved in ProfitYO. Check the invoice list before trying to send it again.`
          );

          navigate(
            '/sales/invoices',
            {
              replace: true,
            }
          );

          return;
        }

        /*
         * Nothing was created yet, so it is safe to
         * remain on this form and let the user correct
         * the problem.
         */
        setError(message);

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } finally {
        setSaving(false);
        setSavingMode(null);
      }
    };

  /*
   * -------------------------------------------------------
   * NO ORGANIZATION
   * -------------------------------------------------------
   */

  if (!organizationId) {
    return (
      <div className="mx-auto max-w-[1300px]">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-semibold text-amber-900">
            No client selected
          </p>

          <p className="mt-1 text-xs text-amber-700">
            Select a client organization before creating an invoice.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate(
                '/sales/invoices'
              )
            }
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-medium text-amber-800"
          >
            <SafeIcon
              icon={FiArrowLeft}
            />

            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * LOADING
   * -------------------------------------------------------
   */

  if (pageLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <SafeIcon
            icon={FiLoader}
            className="mx-auto mb-3 animate-spin text-2xl text-slate-500"
          />

          <p className="text-xs text-slate-500">
            Preparing invoice...
          </p>
        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * PAGE
   * -------------------------------------------------------
   */

  return (
    <div className="mx-auto max-w-[1300px] pb-12">
      {/* PAGE HEADER */}

      <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(
                '/sales/invoices'
              )
            }
            disabled={saving}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon
              icon={FiArrowLeft}
            />
          </button>

          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              Create Invoice
            </h1>

            <p className="mt-0.5 text-xs text-slate-500">
              Create a new invoice for{' '}
              <span className="font-medium text-slate-700">
                {activeClient?.name ||
                  'this client'}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              navigate(
                '/sales/invoices'
              )
            }
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={
              handleSaveDraft
            }
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon
              icon={
                savingMode ===
                'draft'
                  ? FiLoader
                  : FiSave
              }
              className={
                savingMode ===
                'draft'
                  ? 'animate-spin'
                  : ''
              }
            />

            {savingMode ===
            'draft'
              ? 'Saving...'
              : 'Save Draft'}
          </button>
        </div>
      </div>

      {/* ERROR */}

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <SafeIcon
            icon={FiX}
            className="mt-0.5 shrink-0 text-rose-600"
          />

          <div>
            <p className="text-xs font-semibold text-rose-900">
              Unable to save invoice
            </p>

            <p className="mt-1 text-xs leading-5 text-rose-700">
              {error}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* LEFT */}

        <div className="space-y-5">
          {/* CUSTOMER */}

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <SafeIcon
                  icon={FiUser}
                />
              </div>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Customer
                </h2>

                <p className="mt-0.5 text-xs text-slate-400">
                  Choose who this invoice is for.
                </p>
              </div>
            </div>

            <div className="p-5">
              <div className="mb-5 inline-flex rounded-lg bg-slate-100 p-1">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setCustomerMode(
                      'existing'
                    )
                  }
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    customerMode ===
                    'existing'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Existing Customer
                </button>

                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setCustomerMode(
                      'new'
                    )
                  }
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    customerMode ===
                    'new'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  New Customer
                </button>
              </div>

              {customerMode ===
              'existing' ? (
                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Select Customer
                  </label>

                  <div className="relative">
                    <select
                      value={
                        customerId
                      }
                      disabled={saving}
                      onChange={(
                        event
                      ) =>
                        setCustomerId(
                          event.target
                            .value
                        )
                      }
                      className={`${inputClass} appearance-none pr-10`}
                    >
                      <option value="">
                        Select a customer
                      </option>

                      {customers.map(
                        (customer) => (
                          <option
                            key={
                              customer.id
                            }
                            value={
                              customer.id
                            }
                          >
                            {
                              customer.name
                            }
                            {customer.company_name
                              ? ` — ${customer.company_name}`
                              : ''}
                          </option>
                        )
                      )}
                    </select>

                    <SafeIcon
                      icon={
                        FiChevronDown
                      }
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                  </div>

                  {selectedCustomer && (
                    <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-medium text-slate-700">
                        {
                          selectedCustomer.name
                        }
                      </p>

                      {selectedCustomer.company_name && (
                        <p className="mt-1 text-[11px] text-slate-500">
                          {
                            selectedCustomer.company_name
                          }
                        </p>
                      )}

                      {selectedCustomer.email && (
                        <p className="mt-1 text-[11px] text-slate-500">
                          {
                            selectedCustomer.email
                          }
                        </p>
                      )}
                    </div>
                  )}

                  {customers.length ===
                    0 && (
                    <div className="mt-3 rounded-lg border border-dashed border-slate-200 p-4 text-center">
                      <p className="text-xs text-slate-500">
                        No customers found.
                      </p>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          setCustomerMode(
                            'new'
                          )
                        }
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <SafeIcon
                          icon={FiPlus}
                        />

                        Add Customer
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Customer Name *
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.name
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'name',
                          event.target
                            .value
                        )
                      }
                      placeholder="John Smith"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Email *
                    </label>

                    <input
                      type="email"
                      disabled={saving}
                      value={
                        newCustomer.email
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'email',
                          event.target
                            .value
                        )
                      }
                      placeholder="customer@example.com"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Company
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.companyName
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'companyName',
                          event.target
                            .value
                        )
                      }
                      placeholder="Company name"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Phone
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.phone
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'phone',
                          event.target
                            .value
                        )
                      }
                      placeholder="Phone number"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      className={
                        labelClass
                      }
                    >
                      Address
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.addressLine1
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'addressLine1',
                          event.target
                            .value
                        )
                      }
                      placeholder="Street address"
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      className={
                        labelClass
                      }
                    >
                      Address Line 2
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.addressLine2
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'addressLine2',
                          event.target
                            .value
                        )
                      }
                      placeholder="Suite, unit, building, etc."
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      City
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.city
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'city',
                          event.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      State / Province
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.state
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'state',
                          event.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Postal Code
                    </label>

                    <input
                      type="text"
                      disabled={saving}
                      value={
                        newCustomer.postalCode
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'postalCode',
                          event.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Country Code
                    </label>

                    <input
                      type="text"
                      maxLength={2}
                      disabled={saving}
                      value={
                        newCustomer.country
                      }
                      onChange={(
                        event
                      ) =>
                        updateNewCustomer(
                          'country',
                          event.target.value.toUpperCase()
                        )
                      }
                      placeholder="US"
                      className={
                        inputClass
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* INVOICE DETAILS */}

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <SafeIcon
                  icon={FiFileText}
                />
              </div>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Invoice Details
                </h2>

                <p className="mt-0.5 text-xs text-slate-400">
                  Invoice number, dates and currency.
                </p>
              </div>
            </div>

            <div className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Invoice Number
                </label>

                <input
                  type="text"
                  disabled={saving}
                  value={
                    invoiceNumber
                  }
                  onChange={(
                    event
                  ) =>
                    setInvoiceNumber(
                      event.target.value
                    )
                  }
                  placeholder="INV-2026-0001"
                  className={
                    inputClass
                  }
                />
              </div>

              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Issue Date
                </label>

                <div className="relative">
                  <SafeIcon
                    icon={FiCalendar}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="date"
                    disabled={saving}
                    value={issueDate}
                    onChange={(
                      event
                    ) =>
                      setIssueDate(
                        event.target.value
                      )
                    }
                    className={`${inputClass} pl-9`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Due Date
                </label>

                <div className="relative">
                  <SafeIcon
                    icon={FiCalendar}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="date"
                    disabled={saving}
                    value={dueDate}
                    min={issueDate}
                    onChange={(
                      event
                    ) =>
                      setDueDate(
                        event.target.value
                      )
                    }
                    className={`${inputClass} pl-9`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Currency
                </label>

                <select
                  value={currency}
                  disabled={saving}
                  onChange={(
                    event
                  ) =>
                    setCurrency(
                      event.target.value
                    )
                  }
                  className={
                    inputClass
                  }
                >
                  <option value="USD">
                    USD — US Dollar
                  </option>

                  <option value="PHP">
                    PHP — Philippine Peso
                  </option>

                  <option value="EUR">
                    EUR — Euro
                  </option>

                  <option value="GBP">
                    GBP — British Pound
                  </option>

                  <option value="CAD">
                    CAD — Canadian Dollar
                  </option>

                  <option value="AUD">
                    AUD — Australian Dollar
                  </option>
                </select>
              </div>
            </div>
          </section>

          {/* LINE ITEMS */}

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Line Items
                </h2>

                <p className="mt-0.5 text-xs text-slate-400">
                  Add the services or products being billed.
                </p>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={
                  handleAddItem
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiPlus}
                />

                Add Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[minmax(260px,1fr)_110px_160px_160px_42px] gap-3 bg-slate-50 px-5 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Description
                  </p>

                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Qty
                  </p>

                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Rate
                  </p>

                  <p className="text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Amount
                  </p>

                  <div />
                </div>

                <div className="divide-y divide-slate-100">
                  {items.map(
                    (item) => {
                      const quantity =
                        Number(
                          item.quantity ||
                            0
                        );

                      const rate =
                        Number(
                          item.unitAmount ||
                            0
                        );

                      const amount =
                        Number.isFinite(
                          quantity
                        ) &&
                        Number.isFinite(
                          rate
                        )
                          ? quantity *
                            rate
                          : 0;

                      return (
                        <div
                          key={
                            item.id
                          }
                          className="grid grid-cols-[minmax(260px,1fr)_110px_160px_160px_42px] items-center gap-3 px-5 py-3"
                        >
                          <input
                            type="text"
                            disabled={saving}
                            value={
                              item.description
                            }
                            onChange={(
                              event
                            ) =>
                              handleUpdateItem(
                                item.id,
                                'description',
                                event.target
                                  .value
                              )
                            }
                            placeholder="Monthly bookkeeping service"
                            className={
                              inputClass
                            }
                          />

                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            disabled={saving}
                            value={
                              item.quantity
                            }
                            onChange={(
                              event
                            ) =>
                              handleUpdateItem(
                                item.id,
                                'quantity',
                                event.target
                                  .value
                              )
                            }
                            className={
                              inputClass
                            }
                          />

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={saving}
                            value={
                              item.unitAmount
                            }
                            onChange={(
                              event
                            ) =>
                              handleUpdateItem(
                                item.id,
                                'unitAmount',
                                event.target
                                  .value
                              )
                            }
                            placeholder="0.00"
                            className={
                              inputClass
                            }
                          />

                          <p className="text-right text-sm font-medium text-slate-800">
                            {formatMoney(
                              amount,
                              currency
                            )}
                          </p>

                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveItem(
                                item.id
                              )
                            }
                            disabled={
                              saving ||
                              items.length <=
                                1
                            }
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <SafeIcon
                              icon={
                                FiTrash2
                              }
                            />
                          </button>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 px-5 py-3">
              <button
                type="button"
                disabled={saving}
                onClick={
                  handleAddItem
                }
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiPlus}
                />

                Add another line
              </button>
            </div>
          </section>

          {/* NOTES */}

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">
              Notes
            </h2>

            <p className="mt-0.5 text-xs text-slate-400">
              Optional information shown with the invoice.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Memo
                </label>

                <textarea
                  rows={4}
                  disabled={saving}
                  value={memo}
                  onChange={(
                    event
                  ) =>
                    setMemo(
                      event.target.value
                    )
                  }
                  placeholder="Add a note for the customer..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div>
                <label
                  className={
                    labelClass
                  }
                >
                  Footer
                </label>

                <textarea
                  rows={4}
                  disabled={saving}
                  value={footer}
                  onChange={(
                    event
                  ) =>
                    setFooter(
                      event.target.value
                    )
                  }
                  placeholder="Thank you for your business."
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT SUMMARY */}

        <div>
          <div className="sticky top-4 rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold text-slate-900">
                Invoice Summary
              </h2>

              <p className="mt-0.5 text-xs text-slate-400">
                Review the invoice total.
              </p>
            </div>

            <div className="p-5">
              <div className="space-y-4">
                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Tax Amount
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={saving}
                    value={taxAmount}
                    onChange={(
                      event
                    ) =>
                      setTaxAmount(
                        event.target
                          .value
                      )
                    }
                    placeholder="0.00"
                    className={
                      inputClass
                    }
                  />
                </div>

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Discount Amount
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={saving}
                    value={
                      discountAmount
                    }
                    onChange={(
                      event
                    ) =>
                      setDiscountAmount(
                        event.target
                          .value
                      )
                    }
                    placeholder="0.00"
                    className={
                      inputClass
                    }
                  />
                </div>
              </div>

              <div className="my-5 border-t border-slate-100" />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Subtotal
                  </span>

                  <span className="text-xs font-medium text-slate-800">
                    {formatMoney(
                      totals.subtotal,
                      currency
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Tax
                  </span>

                  <span className="text-xs font-medium text-slate-800">
                    {formatMoney(
                      totals.tax,
                      currency
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Discount
                  </span>

                  <span className="text-xs font-medium text-slate-800">
                    -
                    {formatMoney(
                      totals.discount,
                      currency
                    )}
                  </span>
                </div>
              </div>

              <div className="my-5 border-t border-slate-200" />

              <div className="flex items-end justify-between gap-4">
                <span className="text-sm font-semibold text-slate-700">
                  Total
                </span>

                <span className="text-xl font-semibold tracking-tight text-slate-900">
                  {formatMoney(
                    totals.total,
                    currency
                  )}
                </span>
              </div>

              <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50 p-3">
                <div className="flex items-start gap-2">
                  <SafeIcon
                    icon={FiCheck}
                    className="mt-0.5 shrink-0 text-emerald-600"
                  />

                  <p className="text-[11px] leading-5 text-slate-500">
                    Save Draft stores this invoice in ProfitYO without sending it. Save & Send creates the invoice, finalizes it through Stripe, and sends the customer a secure payment link.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={
                  handleSaveDraft
                }
                disabled={saving}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SafeIcon
                  icon={
                    savingMode ===
                    'draft'
                      ? FiLoader
                      : FiSave
                  }
                  className={
                    savingMode ===
                    'draft'
                      ? 'animate-spin'
                      : ''
                  }
                />

                {savingMode ===
                'draft'
                  ? 'Saving Draft...'
                  : 'Save Draft'}
              </button>

              <button
                type="button"
                onClick={
                  handleSaveAndSend
                }
                disabled={saving}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-900 bg-white px-4 py-2.5 text-xs font-medium text-slate-900 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SafeIcon
                  icon={
                    savingMode ===
                    'send'
                      ? FiLoader
                      : FiSend
                  }
                  className={
                    savingMode ===
                    'send'
                      ? 'animate-spin'
                      : ''
                  }
                />

                {savingMode ===
                'send'
                  ? 'Saving & Sending...'
                  : 'Save & Send'}
              </button>

              <p className="mt-2 text-center text-[10px] leading-4 text-slate-400">
                Save & Send requires a customer email address.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}